import { DateTime } from "luxon";
import {
	agencyNameOf,
	approvedNurseIdsOf,
	autoPostNumbers,
	between,
	CALL_OFF_CHANNELS,
	COIN_FLIP_PERCENT,
	creatorOf,
	type DemoOriginsContext,
	hppdNumbers,
	leadMinutesOf,
	percent,
	pickRosterNurse,
	posterOf,
	recommendationDayOf,
	rosterNurseById,
	seededIndex,
	seededInt,
	timeOffCandidate,
	toPerson,
	weightedKind,
} from "./demoRequestOriginSeed";
import {
	ChangesSource,
	type LedgerEntry,
	NotificationType,
	type OriginPerson,
	type RequestLedger,
	RequestOriginKind,
	RequestSource,
	RequestStatus,
	type ShiftRequest,
} from "./models";

/**
 * Demo-only request ledgers, in the API's shape, for `inhouse\d*__` departments (the API answers them 404). Real fields
 * decide the cause wherever they name one (a call-off, a replace request, a Clipboard shift); a plain post is given a
 * seeded one. Approved answers become fills, so the entries add up to the request's open slots like the API's do.
 */
type SharedFields = Pick<LedgerEntry, "at" | "delta" | "lead_minutes" | "batch_id" | "batch_size">;
type BuildOptions = { salt: string; excludeNurseId: number | null; allowRatio: boolean; total: number };

/** How many posted requests get a second cause, and within how long after the first. */
const SECOND_CAUSE_PERCENT = 30;
const SECOND_CAUSE_WINDOW_MINUTES = 6 * 60;
const SECOND_CAUSE_EARLIEST_FRACTION = 0.1;
const CLIPBOARD_AGENCY_NAME = "Clipboard";
const AUTO_POST_BATCH = { MIN: 1, MAX: 4 } as const;
const DEACTIVATION_SHIFTS = { MIN: 1, MAX: 6 } as const;
const RECOMMENDED_EXTRA = { MIN: 0, MAX: 1 } as const;

/** The cause the request's own fields name, or `null` for a plain post, which is seeded instead. */
const realKindOf = (request: ShiftRequest): RequestOriginKind | null => {
	if (request.request_type === NotificationType.ReplaceShift) return RequestOriginKind.ReplaceRequest;
	if (request.request_type === NotificationType.Swap) return RequestOriginKind.SwapRequest;
	if (request.request_type === NotificationType.ReplaceAgency) return RequestOriginKind.ReplacingAgency;
	if (request.source === null || request.source === undefined) return agencyNameOf(request) === null ? null : RequestOriginKind.Agency;
	switch (request.source) {
		case RequestSource.CallOff:
			return RequestOriginKind.CallOff;
		case RequestSource.Pto:
			return RequestOriginKind.TimeOff;
		case RequestSource.Clipboard:
			return RequestOriginKind.Agency;
		case RequestSource.CancelAgency:
			return RequestOriginKind.ReplacingAgency;
		case RequestSource.PostShiftRecommendation:
			return RequestOriginKind.FromRecommendation;
		// The demo seeder writes every open shift it creates as an auto-post, so read literally a demo list would be one
		// kind top to bottom; plain and seeder posts get the seeded spread instead.
		case RequestSource.PostShift:
		case RequestSource.AutoPostShift:
			return agencyNameOf(request) === null ? null : RequestOriginKind.Agency;
		default: {
			const unhandled: never = request.source;
			throw new Error(`Unhandled request source ${String(unhandled)}`);
		}
	}
};

/** A cause that names a nurse, or `null` when the roster has nobody to name. */
const nurseEntry = (
	kind: RequestOriginKind,
	request: ShiftRequest,
	shared: SharedFields,
	ctx: DemoOriginsContext,
	options: BuildOptions,
): LedgerEntry | null => {
	const rosterNurse = pickRosterNurse(request, `${options.salt}:nurse`, ctx, options.excludeNurseId);
	if (rosterNurse === null) return null;
	const nurse = toPerson(rosterNurse);
	const { shift_start, shift_end } = request.message;
	switch (kind) {
		case RequestOriginKind.CallOff: {
			const channel = CALL_OFF_CHANNELS[seededIndex(CALL_OFF_CHANNELS.length, `${request.id}:${options.salt}:channel`)];
			const actor = channel === ChangesSource.Staffing ? posterOf(request, ctx) : nurse;
			return { ...shared, kind, actor, details: { kind, nurse, shift_start, shift_end, channel } };
		}
		case RequestOriginKind.TimeOff:
			return { ...shared, kind, actor: creatorOf(request, ctx), details: { kind, candidates: [timeOffCandidate(request, nurse, ctx)], ambiguous: false } };
		case RequestOriginKind.Deactivation:
			return {
				...shared,
				kind,
				actor: creatorOf(request, ctx),
				details: { kind, nurse, shifts_removed: seededInt(request, "shifts-removed", DEACTIVATION_SHIFTS) },
			};
		default:
			return null;
	}
};

/** The staff member behind a replace or swap request: its own nurse, or a seeded one of the right role. */
const requesterOf = (kind: RequestOriginKind, request: ShiftRequest, ctx: DemoOriginsContext): OriginPerson | null => {
	const own =
		kind === RequestOriginKind.ReplaceRequest && "replacement_nurse_id" in request
			? rosterNurseById(ctx, request.replacement_nurse_id)
			: (ctx.roster.find((nurse) => nurse.userId === request.created_by) ?? null);
	const nurse = own !== null ? own : pickRosterNurse(request, "requester", ctx, null);
	return nurse === null ? null : toPerson(nurse);
};

const replacingAgencyEntry = (request: ShiftRequest, shared: SharedFields, ctx: DemoOriginsContext, salt: string): LedgerEntry | null => {
	const replaced = "replacement_nurse_id" in request ? rosterNurseById(ctx, request.replacement_nurse_id) : null;
	const nurse = replaced !== null ? replaced : pickRosterNurse(request, `${salt}:agency-nurse`, ctx, null);
	if (nurse === null) return null;
	const kind = RequestOriginKind.ReplacingAgency;
	const agencyName = agencyNameOf(request);
	// DEFAULT: demo only — a request with no stored agency name takes its agency nurse's agency; with neither, null, and
	// the card says "agency staff" without naming one, as the API's null does.
	return {
		...shared,
		kind,
		actor: null,
		details: { kind, nurse: toPerson(nurse), agency_name: agencyName !== null ? agencyName : (nurse.agencyName ?? null) },
	};
};

const buildEntry = (
	kind: RequestOriginKind,
	request: ShiftRequest,
	shared: SharedFields,
	ctx: DemoOriginsContext,
	options: BuildOptions,
): LedgerEntry | null => {
	const slots = shared.delta;
	switch (kind) {
		case RequestOriginKind.CallOff:
		case RequestOriginKind.TimeOff:
		case RequestOriginKind.Deactivation:
			return nurseEntry(kind, request, shared, ctx, options);
		case RequestOriginKind.ReplacingAgency:
			return replacingAgencyEntry(request, shared, ctx, options.salt);
		case RequestOriginKind.AutoPost: {
			const numbers = autoPostNumbers(request, ctx, options.allowRatio);
			const batchSize = seededInt(request, "batch", AUTO_POST_BATCH);
			return { ...shared, batch_size: batchSize, kind, actor: null, details: { kind, numbers, posted_count: slots, recommended_count: slots } };
		}
		case RequestOriginKind.FromRecommendation: {
			const recommended = slots + seededInt(request, "recommended-extra", RECOMMENDED_EXTRA);
			const details = { kind, numbers: hppdNumbers(request.id, recommendationDayOf(request, ctx)), posted_count: slots, recommended_count: recommended };
			return { ...shared, kind, actor: posterOf(request, ctx), details };
		}
		case RequestOriginKind.Agency: {
			// DEFAULT: demo only — a Clipboard-sourced request without a stored agency name is Clipboard's.
			const agencyName = agencyNameOf(request) ?? CLIPBOARD_AGENCY_NAME;
			return { ...shared, kind, actor: null, details: { kind, agency_name: agencyName, from_count: 0, to_count: slots } };
		}
		case RequestOriginKind.CountRaised:
			return { ...shared, kind, actor: posterOf(request, ctx), details: { kind, from_count: options.total - 1, to_count: options.total } };
		case RequestOriginKind.ReplaceRequest:
		case RequestOriginKind.SwapRequest: {
			const nurse = requesterOf(kind, request, ctx);
			if (nurse === null) return null;
			const { shift_start, shift_end } = request.message;
			const details =
				kind === RequestOriginKind.ReplaceRequest ? { kind, nurse, shift_start, shift_end } : { kind, nurse, shift_start, shift_end, counterpart: null };
			return { ...shared, kind, actor: nurse, details };
		}
		case RequestOriginKind.PostedByPerson:
		case RequestOriginKind.PostedByAnnie:
			return { ...shared, kind, actor: posterOf(request, ctx), details: { kind } };
		default:
			throw new Error(`The demo does not seed ${kind}`);
	}
};

/** A cause that names a nurse, with no eligible roster nurse, is told as a manual post rather than dropped. */
const buildOrPost = (kind: RequestOriginKind, request: ShiftRequest, shared: SharedFields, ctx: DemoOriginsContext, options: BuildOptions): LedgerEntry => {
	const entry = buildEntry(kind, request, shared, ctx, options);
	if (entry !== null) return entry;
	const posted = RequestOriginKind.PostedByPerson;
	return { ...shared, kind: posted, actor: posterOf(request, ctx), details: { kind: posted } };
};

/** Which cause added the request's later slot, and when — often automation answering a call-off, or the reverse. */
const secondCauseOf = (
	request: ShiftRequest,
	firstKind: RequestOriginKind,
	total: number,
	ctx: DemoOriginsContext,
): { kind: RequestOriginKind; at: DateTime } | null => {
	if (total < 2 || percent(request, "second") >= SECOND_CAUSE_PERCENT) return null;
	const fraction = between(request, "second-at", SECOND_CAUSE_EARLIEST_FRACTION, 1);
	const at = request.created_at.plus({ minutes: Math.round(fraction * SECOND_CAUSE_WINDOW_MINUTES) });
	if (at > ctx.now || at >= request.message.shift_start) return null;
	switch (firstKind) {
		case RequestOriginKind.CallOff:
			return { kind: RequestOriginKind.AutoPost, at };
		case RequestOriginKind.AutoPost:
			return { kind: RequestOriginKind.CallOff, at };
		case RequestOriginKind.PostedByPerson:
			return { kind: percent(request, "second-kind") < COIN_FLIP_PERCENT ? RequestOriginKind.AutoPost : RequestOriginKind.CountRaised, at };
		default:
			return null;
	}
};

const shared = (request: ShiftRequest, at: DateTime, delta: number): SharedFields => ({
	at,
	delta,
	lead_minutes: leadMinutesOf(request, at),
	batch_id: null,
	batch_size: null,
});

/** One fill per approved answer. The demo has no approval timestamp, so use the latest known opening time. */
const fillEntries = (request: ShiftRequest, after: DateTime, ctx: DemoOriginsContext): LedgerEntry[] => {
	const approver = posterOf(request, ctx);
	return (
		approvedNurseIdsOf(request)
			.map((nurseId): LedgerEntry => {
				const nurse = rosterNurseById(ctx, nurseId);
				const kind = RequestOriginKind.Filled;
				// DEFAULT: demo only — an approved nurse no longer on the roster is named by id, the only name the demo has.
				const person: OriginPerson = nurse !== null ? toPerson(nurse) : { nurse_id: nurseId, first_name: String(nurseId), last_name: null };
				return { ...shared(request, after, -1), kind, actor: approver, details: { kind, nurse: person, approved_via: ChangesSource.Requests } };
			})
	);
};

/**
 * Slots add up to the request: all it ever opened is what is still open plus what was filled. The later cause adds
 * one; the first opened the rest; each approved answer took one away.
 */
export const demoLedgerOf = (request: ShiftRequest, ctx: DemoOriginsContext): RequestLedger => {
	const fills = approvedNurseIdsOf(request);
	const stillNeeded =
		request.request_type === NotificationType.OpenShift && "needed_nurses" in request
			? request.needed_nurses
			: // DEFAULT: a replace or swap request holds exactly one slot, open until its one answer is approved.
				1 - fills.length;
	const total = stillNeeded + fills.length;
	const realKind = realKindOf(request);
	const firstKind = realKind === null ? weightedKind(request, "kind") : realKind;
	const second = secondCauseOf(request, firstKind, total, ctx);
	const firstSlots = Math.max(1, total - (second === null ? 0 : 1));
	const first = buildOrPost(firstKind, request, shared(request, request.created_at, firstSlots), ctx, {
		salt: "first",
		excludeNurseId: null,
		allowRatio: firstSlots === 1,
		total,
	});
	const entries: LedgerEntry[] = [first];
	if (second !== null) {
		const excludeNurseId = first.details.kind === RequestOriginKind.CallOff ? first.details.nurse.nurse_id : null;
		entries.push(buildOrPost(second.kind, request, shared(request, second.at, 1), ctx, { salt: "second", excludeNurseId, allowRatio: true, total }));
	}
	const lastOpeningAt = second === null ? request.created_at : second.at;
	entries.push(...fillEntries(request, lastOpeningAt, ctx));
	const openNow = request.status === RequestStatus.Active ? stillNeeded : 0;
	const leftOver = entries.reduce((sum, entry) => sum + entry.delta, 0) - openNow;
	if (leftOver > 0) {
		const kind = RequestOriginKind.Canceled;
		entries.push({
			...shared(request, lastOpeningAt, -leftOver),
			kind,
			actor: posterOf(request, ctx),
			details: { kind, source: ChangesSource.Requests },
		});
	}
	return { request_id: request.id, status: request.status, open_slots_now: openNow, recorded_from: request.created_at, entries };
};

/** What a request's ledger is seeded from, so an unchanged request keeps the same objects across refreshes. */
export const demoOriginsSignature = (request: ShiftRequest): string =>
	JSON.stringify([
		request.created_at.toMillis(),
		request.created_at.zoneName,
		request.request_type,
		request.source,
		request.status,
		request.created_by,
		request.job_id,
		request.location,
		request.message.roles,
		request.message.shift_start.toMillis(),
		request.message.shift_start.zoneName,
		"agency_source_name" in request ? request.agency_source_name : null,
		"replacement_nurse_id" in request ? request.replacement_nurse_id : null,
		"needed_nurses" in request ? request.needed_nurses : null,
		approvedNurseIdsOf(request),
	]);
